import { useEffect, useState } from "react";

/**
 * Discreet sticky table of contents for long editorial pages.
 *
 * - Visible only on >= xl breakpoints (1280px+) — hidden on tablet/mobile
 *   to keep main content readable.
 * - Highlights the currently visible section using IntersectionObserver.
 * - Smooth scroll on click, with a small header offset.
 *
 * Props:
 *   items: [{ id: string, label: string }]
 */
export default function TableOfContents({ items = [], label = "Sommaire" }) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? null);

  useEffect(() => {
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the largest intersection ratio currently in view.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        // Trigger when the section is roughly in the upper half of the viewport.
        rootMargin: "-30% 0px -55% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    items.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [items]);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: "smooth" });
  };

  if (!items.length) return null;

  return (
    <nav
      data-testid="toc"
      aria-label={label}
      className="hidden min-[1600px]:block fixed left-6 top-1/2 -translate-y-1/2 z-30 w-[150px]"
    >
      <p className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#8B8F95] mb-4">
        {label}
      </p>
      <ol className="space-y-2 border-l border-[#E2DEDA]">
        {items.map((it, i) => {
          const active = it.id === activeId;
          return (
            <li key={it.id} className="relative">
              <a
                href={`#${it.id}`}
                onClick={(e) => handleClick(e, it.id)}
                data-testid={`toc-link-${it.id}`}
                aria-current={active ? "true" : undefined}
                className={`block pl-3 py-1 text-[10.5px] leading-snug tracking-wide transition-colors ${
                  active
                    ? "text-[#181A1D] font-medium"
                    : "text-[#666A70] hover:text-[#181A1D]"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`absolute left-[-1px] top-1/2 -translate-y-1/2 h-3 w-[2px] transition-colors ${
                    active ? "bg-[#181A1D]" : "bg-transparent"
                  }`}
                />
                <span className="font-mono text-[9px] tracking-[0.22em] text-[#B4B8BD] mr-1.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
